/**
 * TeamReportTable Component - Team report with sortable table
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Users, Search, X, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { colors } from './constants';

const TeamReportTable = ({ 
  teams, 
  onUserClick 
}) => {
  const [teamSearch, setTeamSearch] = useState('');
  const [teamSortBy, setTeamSortBy] = useState('rate');
  const [teamSortDir, setTeamSortDir] = useState('desc');
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [showAllUsers, setShowAllUsers] = useState({});

  // Sorted and filtered teams
  const sortedTeams = useMemo(() => {
    if (!teams) return [];
    
    let filtered = [...teams];
    
    // Filter by search term
    if (teamSearch.trim()) {
      const search = teamSearch.toLowerCase();
      filtered = filtered.filter(t => t.team.toLowerCase().includes(search));
    }
    
    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      if (teamSortBy === 'rate') {
        comparison = (a.completion_rate || 0) - (b.completion_rate || 0);
      } else if (teamSortBy === 'name') {
        comparison = a.team.localeCompare(b.team);
      } else if (teamSortBy === 'total') {
        comparison = (a.total || 0) - (b.total || 0);
      }
      return teamSortDir === 'desc' ? -comparison : comparison;
    });
    
    return filtered;
  }, [teams, teamSortBy, teamSortDir, teamSearch]);

  const handleTeamSort = (column) => {
    if (teamSortBy === column) {
      setTeamSortDir(teamSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setTeamSortBy(column);
      setTeamSortDir('desc');
    }
  };

  if (!teams || teams.length === 0) return null;

  return (
    <Card className="bg-white md:col-span-2" style={{ borderColor: colors.border }}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-base" style={{ color: colors.dark }}>
            Teams Übersicht ({sortedTeams.length}/{teams.length})
          </CardTitle>
          {/* Team Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
            <input
              type="text"
              placeholder="Team suchen..."
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              className="w-full pl-9 pr-8 py-1.5 rounded-lg border text-sm"
              style={{ borderColor: colors.border, color: colors.dark }}
              data-testid="team-search-input"
            />
            {teamSearch && (
              <button 
                onClick={() => setTeamSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2"
              >
                <X className="h-4 w-4" style={{ color: colors.gray }} />
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b text-xs font-medium" 
             style={{ borderColor: colors.border, background: colors.bg, color: colors.gray }}>
          <div 
            className="col-span-5 flex items-center gap-1 cursor-pointer hover:text-gray-900"
            onClick={() => handleTeamSort('name')}
          >
            Team/Abteilung
            {teamSortBy === 'name' && (teamSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
          </div>
          <div 
            className="col-span-2 text-center flex items-center justify-center gap-1 cursor-pointer hover:text-gray-900"
            onClick={() => handleTeamSort('total')}
          >
            Gesamt
            {teamSortBy === 'total' && (teamSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
          </div>
          <div className="col-span-1 text-center text-green-600">Gültig</div>
          <div className="col-span-1 text-center text-red-600">Abgelaufen</div>
          <div className="col-span-1 text-center text-orange-600">Offen</div>
          <div 
            className="col-span-2 text-center flex items-center justify-center gap-1 cursor-pointer hover:text-gray-900"
            onClick={() => handleTeamSort('rate')}
          >
            Quote
            {teamSortBy === 'rate' && (teamSortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
          </div>
        </div>
        
        {/* Table Body */}
        <ScrollArea className="h-[400px]">
          <div className="divide-y" style={{ borderColor: colors.border }}>
            {sortedTeams.map((team, idx) => (
              <TeamRow 
                key={idx}
                team={team}
                idx={idx}
                isExpanded={expandedTeam === idx}
                onToggleExpand={() => setExpandedTeam(expandedTeam === idx ? null : idx)}
                showAllUsers={showAllUsers}
                setShowAllUsers={setShowAllUsers}
                onUserClick={onUserClick}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

const TeamRow = ({ 
  team, 
  idx, 
  isExpanded, 
  onToggleExpand, 
  showAllUsers, 
  setShowAllUsers, 
  onUserClick 
}) => (
  <div>
    <div 
      className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-gray-50 cursor-pointer items-center"
      onClick={onToggleExpand}
    >
      <div className="col-span-5 flex items-center gap-2">
        <Users className="h-4 w-4 flex-shrink-0" style={{ color: colors.blue }} />
        <span className="text-sm truncate" style={{ color: colors.dark }} title={team.team}>
          {team.team}
        </span>
        {isExpanded ? 
          <ChevronDown className="h-4 w-4 flex-shrink-0" style={{ color: colors.gray }} /> : 
          <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: colors.gray }} />
        }
      </div>
      <div className="col-span-2 text-center text-sm" style={{ color: colors.gray }}>
        {team.total}
      </div>
      <div className="col-span-1 text-center text-sm font-medium text-green-600">
        {team.completed}
      </div>
      <div className="col-span-1 text-center text-sm font-medium text-red-600">
        {team.expired || 0}
      </div>
      <div className="col-span-1 text-center text-sm font-medium text-orange-600">
        {team.not_completed}
      </div>
      <div className="col-span-2 flex justify-center">
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full"
              style={{ 
                width: `${team.completion_rate}%`,
                background: team.completion_rate >= 80 ? '#22c55e' : team.completion_rate >= 50 ? '#f59e0b' : '#ef4444'
              }}
            />
          </div>
          <span 
            className="text-xs font-bold min-w-[36px] text-right"
            style={{ color: team.completion_rate >= 80 ? '#22c55e' : team.completion_rate >= 50 ? '#f59e0b' : '#ef4444' }}
          >
            {team.completion_rate}%
          </span>
        </div>
      </div>
    </div>
    
    {/* Expanded Details */}
    {isExpanded && (
      <TeamExpandedDetails 
        team={team} 
        idx={idx} 
        showAllUsers={showAllUsers} 
        setShowAllUsers={setShowAllUsers} 
        onUserClick={onUserClick}
      />
    )}
  </div>
);

const TeamExpandedDetails = ({ team, idx, showAllUsers, setShowAllUsers, onUserClick }) => (
  <div className="px-4 pb-4 bg-gray-50 border-t" style={{ borderColor: colors.border }}>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
      {/* Expired */}
      {team.members_expired?.length > 0 && (
        <MembersList
          title="Abgelaufen"
          members={team.members_expired}
          color="red"
          showAll={showAllUsers[`${idx}_exp`]}
          onToggleShowAll={() => setShowAllUsers({...showAllUsers, [`${idx}_exp`]: !showAllUsers[`${idx}_exp`]})}
          onUserClick={onUserClick}
          limit={10}
        />
      )}
      
      {/* Not Completed */}
      {team.members_not_completed?.length > 0 && (
        <MembersList
          title="Offen"
          members={team.members_not_completed}
          color="orange"
          showAll={showAllUsers[`${idx}_not`]}
          onToggleShowAll={() => setShowAllUsers({...showAllUsers, [`${idx}_not`]: !showAllUsers[`${idx}_not`]})}
          onUserClick={onUserClick}
          limit={10}
        />
      )}
      
      {/* Completed */}
      {team.members_completed?.length > 0 && (
        <MembersList
          title="Gültig"
          members={team.members_completed}
          color="green"
          showAll={showAllUsers[`${idx}_done`]}
          onToggleShowAll={() => setShowAllUsers({...showAllUsers, [`${idx}_done`]: !showAllUsers[`${idx}_done`]})}
          onUserClick={onUserClick}
          limit={5}
        />
      )}
    </div>
  </div>
);

const MembersList = ({ title, members, color, showAll, onToggleShowAll, onUserClick, limit }) => {
  const colorClasses = {
    red: { text: 'text-red-600', border: 'border-red-200' },
    orange: { text: 'text-orange-600', border: 'border-orange-200' },
    green: { text: 'text-green-600', border: 'border-green-200' }
  };
  const classes = colorClasses[color];

  return (
    <div>
      <p className={`text-sm font-medium mb-2 ${classes.text}`}>
        {title} ({members.length})
      </p>
      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {(showAll ? members : members.slice(0, limit)).map((user, i) => (
          <div key={i} className={`text-xs p-2 bg-white rounded border ${classes.border}`}>
            <span 
              className="cursor-pointer hover:underline"
              style={{ color: colors.dark }}
              onClick={() => onUserClick(user)}
            >
              {user.user_name || 'Unbekannt'}
            </span>
            {user.course_name && <span className="ml-2 text-gray-400">• {user.course_name}</span>}
          </div>
        ))}
        {members.length > limit && !showAll && (
          <button 
            onClick={onToggleShowAll}
            className="text-xs text-center py-2 w-full hover:bg-white rounded cursor-pointer"
            style={{ color: colors.blue }}
          >
            + {members.length - limit} weitere anzeigen
          </button>
        )}
        {showAll && (
          <button 
            onClick={onToggleShowAll}
            className="text-xs text-center py-2 w-full hover:bg-white rounded cursor-pointer"
            style={{ color: colors.gray }}
          >
            Weniger anzeigen
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamReportTable;
